import { Plus, Trash, UsersThree } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  useAddThinkerToWorkspace,
  useAvailableThinkers,
  useMembers,
  useRemoveThinkerFromWorkspace,
} from "~/api/queries";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Select } from "~/features/settings/controls";
import { Avatar } from "~/foundation/components/Avatar";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";

/** A human assistant's thinker id is `user:{guid}` — this recovers the person behind it. */
const userIdOf = (thinkerId: string) =>
  thinkerId.startsWith("user:") ? thinkerId.slice("user:".length) : thinkerId;

/**
 * Teammates who can be addressed in a conversation the way a persona can.
 *
 * The area tags are the point, not decoration: they are what `thinkers/suggested/{documentId}`
 * matches against, so tagging somebody "Legal" is what makes them surface on a contract. An
 * assistant with no tags is still addressable — it just never gets suggested.
 */
export function AssistantsPanel({ canAdminister }: { canAdminister: boolean }) {
  const { t } = useTranslation();
  const { data: available, isLoading } = useAvailableThinkers();
  const { data: members } = useMembers();
  const add = useAddThinkerToWorkspace();
  const remove = useRemoveThinkerFromWorkspace();

  const [userId, setUserId] = useState("");
  const [areas, setAreas] = useState("");

  if (isLoading) {
    return <Spinner label={t("Loading")} />;
  }

  const assistants = (available ?? []).filter((thinker) => thinker.isHuman);
  const taken = new Set(assistants.map((assistant) => userIdOf(assistant.id)));
  const candidates = (members ?? []).filter((member) => !member.suspended && !taken.has(member.id));

  const submit = () => {
    if (!userId) {
      return;
    }
    add.mutate(
      {
        kind: "human",
        humanUserId: userId,
        areaTags: areas
          .split(",")
          .map((area) => area.trim())
          .filter(Boolean),
      },
      {
        onSuccess: () => {
          toast.success(t("Assistant added"));
          setUserId("");
          setAreas("");
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <Text variant="small">
        {t(
          "Teammates who can be addressed in conversations. Tag them with topic areas so the right person surfaces where they are relevant.",
        )}
      </Text>

      {canAdminister ? (
        <div className="flex flex-wrap items-end gap-2">
          <Select
            label={t("Teammate")}
            value={userId}
            className="min-w-48"
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="">{t("Choose a teammate")}</option>
            {candidates.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </Select>
          <Input
            label={t("Areas")}
            value={areas}
            className="min-w-48"
            placeholder={t("e.g. Legal, Infra")}
            onChange={(event) => setAreas(event.target.value)}
          />
          <Button
            variant="primary"
            icon={Plus}
            disabled={!userId || add.isPending}
            onClick={submit}
          >
            {t("Add")}
          </Button>
        </div>
      ) : null}

      {assistants.length === 0 ? (
        <Empty icon={UsersThree} message={t("No assistants yet.")} />
      ) : (
        <Rows>
          {assistants.map((assistant) => (
            <Row key={assistant.id}>
              <Avatar name={assistant.name} src={assistant.avatarUrl} size={28} />
              <div className="min-w-0 flex-1">
                <p className="truncate text-body text-text">{assistant.name}</p>
                <p className="truncate text-small text-text-tertiary">
                  {assistant.areaTags.length > 0
                    ? assistant.areaTags.join(", ")
                    : t("No topic areas — never suggested automatically")}
                </p>
              </div>
              <Pill>{t("Person")}</Pill>
              {canAdminister ? (
                <IconButton
                  icon={Trash}
                  aria-label={t("Remove")}
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(assistant.id, {
                      onSuccess: () => toast.success(t("Assistant removed")),
                    })
                  }
                />
              ) : null}
            </Row>
          ))}
        </Rows>
      )}
    </div>
  );
}
